'use client';

import { c as _c } from "react/compiler-runtime";
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContentWrapper, DialogTitle, DialogTrigger } from '@/comps/primitives/dialog';
import { Input } from '@/comps/primitives/input';
import { Button } from '@/comps/primitives/button';
import { useCreateProject } from '@/api/generated/projects/projects';
import { createProjectFormSchema, CreateProjectFormValues, ORG_PROJECT_NAME_MAX_LENGTH } from '@/domains/dashboard/components/create-organization-project.schema';
import { DEFAULT_CREATE_PROJECT_PRODUCT, ProjectProductSelector } from '@/domains/dashboard/components/project-product-selector';
import { NavElement } from '@/domains/home/components/home-tabs/_components/nav-button';
import { getFormFieldErrorMessage } from '@/domains/reports/schemas/report-identity.schema';
import { useProjectsContext } from '@/providers/projects-provider';
import { useForm } from '@tanstack/react-form';
import { Folder, Loader2 } from 'lucide-react';
export type CreateProjectModalProps = PropsWithChildren & {
  organizationId: string;
  onCancel?: () => void;
  onConfirm?: (projectId: string) => void;
};
export function CreateProjectModal(t0) {
  const $ = _c(48);
  const {
    children,
    organizationId,
    onCancel,
    onConfirm
  } = t0;
  const [open, setOpen] = useState(false);
  const {
    selectedProject
  } = useProjectsContext();
  const {
    mutateAsync: createProject,
    isLoading: isCreating
  } = useCreateProject();
  let t1;
  if ($[0] !== selectedProject?.product) {
    t1 = () => ({
      projectName: "",
      product: selectedProject?.product ?? DEFAULT_CREATE_PROJECT_PRODUCT
    });
    $[0] = selectedProject?.product;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  selectedProject?.product;
  const getDefaultValues = t1;
  let t2;
  if ($[2] !== getDefaultValues) {
    t2 = getDefaultValues();
    $[2] = getDefaultValues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onSubmit: createProjectFormSchema
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const form = useForm({
    defaultValues: t2,
    validators: t3,
    onSubmit: async t4 => {
      const {
        value
      } = t4;
      if (isCreating || !organizationId) {
        return;
      }
      const {
        project
      } = await createProject({
        data: {
          name: value.projectName.trim(),
          organization_id: organizationId,
          product: value.product
        }
      });
      if (!project.id) {
        console.error("Error creating project!");
        return;
      }
      setOpen(false);
      form.reset(getDefaultValues());
      onConfirm?.(project.id);
    }
  });
  let t5;
  if ($[5] !== form || $[6] !== getDefaultValues) {
    t5 = () => {
      form.reset(getDefaultValues());
    };
    $[5] = form;
    $[6] = getDefaultValues;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  form;
  const resetForm = t5;
  let t6;
  let t7;
  if ($[8] !== open || $[9] !== resetForm) {
    t6 = () => {
      if (open) {
        resetForm();
      }
    };
    t7 = [open, resetForm];
    $[8] = open;
    $[9] = resetForm;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== resetForm) {
    t8 = nextOpen => {
      setOpen(nextOpen);
      if (!nextOpen) {
        resetForm();
      }
    };
    $[12] = resetForm;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[14] !== handleOpenChange || $[15] !== onCancel) {
    t9 = () => {
      onCancel?.();
      handleOpenChange(false);
    };
    $[14] = handleOpenChange;
    $[15] = onCancel;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleCancel = t9;
  let t10;
  if ($[17] !== children) {
    t10 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[17] = children;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogTitle><div className="flex items-center border-b px-6"><NavElement Icon={Folder} text="Create new project" coloredIcon={true} isActive={true} showTooltip={false} /></div></DialogTitle>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== form.Field) {
    t12 = <form.Field name="projectName">{_temp}</form.Field>;
    $[20] = form.Field;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== form.Field) {
    t13 = <form.Field name="product">{_temp2}</form.Field>;
    $[22] = form.Field;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <div className="flex flex-col gap-4 px-6 py-4">{t12}{t13}</div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== handleCancel || $[28] !== isCreating) {
    t15 = <Button onClick={handleCancel} variant="outline" disabled={isCreating}>Cancel</Button>;
    $[27] = handleCancel;
    $[28] = isCreating;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== form || $[31] !== isCreating || $[32] !== organizationId) {
    t16 = isSubmitting => <Button disabled={isSubmitting || isCreating || !organizationId} onClick={() => void form.handleSubmit()} variant="default">{isCreating ? <><Loader2 className="mr-2 size-4 animate-spin" />Creating…</> : "Create"}</Button>;
    $[30] = form;
    $[31] = isCreating;
    $[32] = organizationId;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== form.Subscribe || $[35] !== t16) {
    t17 = <form.Subscribe selector={_temp3}>{t16}</form.Subscribe>;
    $[34] = form.Subscribe;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t17) {
    t18 = <div className="flex items-center justify-end gap-2 border-t px-6 py-3">{t15}{t17}</div>;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t14 || $[41] !== t18) {
    t19 = <DialogContentWrapper className="max-w-130 gap-0 p-0" container={document.body}><div className="flex flex-col">{t11}{t14}{t18}</div></DialogContentWrapper>;
    $[40] = t14;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== handleOpenChange || $[44] !== open || $[45] !== t10 || $[46] !== t19) {
    t20 = <Dialog open={open} onOpenChange={handleOpenChange}>{t10}{t19}</Dialog>;
    $[43] = handleOpenChange;
    $[44] = open;
    $[45] = t10;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  return t20;
}
function _temp3(state) {
  return state.isSubmitting;
}
function _temp2(field_0) {
  return <ProjectProductSelector value={field_0.state.value} onChange={field_0.handleChange} />;
}
function _temp(field) {
  return <div className="flex flex-col gap-1.5"><label htmlFor="create-project-modal--name" className="text-xs font-medium text-muted-foreground">Project name</label><Input id="create-project-modal--name" value={field.state.value} onChange={e => field.handleChange(e.currentTarget.value)} onBlur={field.handleBlur} placeholder="My new AI agent..." maxLength={ORG_PROJECT_NAME_MAX_LENGTH} invalid={field.state.meta.errors.length > 0} />{field.state.meta.errors.length > 0 && <p className="text-xs text-destructive">{getFormFieldErrorMessage(field.state.meta.errors)}</p>}</div>;
}